// ─────────────────────────────────────────────────────────────────────────────
// buildUserDetail.js — turns a directory user row into the view-model the detail
// modal renders. Real fields (name, email, status, role, team, last activity) are
// read straight from the row; everything the list endpoint doesn't expose yet
// (phone, location, permissions, sessions, activity, policies) is filled with
// presentational placeholders. This is the ONLY place mock data lives — when the
// backend grows a `GET /users/:id` detail payload, swap the placeholders here and
// no tab component has to change.
// ─────────────────────────────────────────────────────────────────────────────
import {
  userName,
  userInitials,
  formatRelative,
} from "../../../../services/usersService";
import { STATUS_CONFIG } from "../badges";

// Live session region if the API ever provides one (mirrors UsersTable).
const sessionRegion = (u) =>
  u.session || u.activeSession?.nodeRegion || u.sessionRegion || u.nodeRegion || null;

export function buildUserDetail(user) {
  if (!user) return null;

  const status = user.status || "offline";
  const statusCfg = STATUS_CONFIG[status] || STATUS_CONFIG.offline;
  const region = sessionRegion(user);

  return {
    // ── Identity (real) ──
    name: userName(user),
    email: user.email,
    initials: userInitials(user),
    status,
    statusLabel: statusCfg.label,
    statusColor: statusCfg.color,
    statusBg: statusCfg.bg,
    statusBorder: statusCfg.border,

    // ── Profile tab ──
    profile: {
      role: user.role || "—",
      phone: user.phone || "+1 (555) 123-4567",
      location: user.location || "San Francisco, CA",
      lastActive: formatRelative(user.lastActiveAt),
      sessionRegion: region || "eu-west-1",
      details: [
        { label: "Team", value: user.team || "—" },
        { label: "Policy", value: user.policy || "Default Access" },
        { label: "MFA", value: user.mfaEnabled === false ? "Disabled" : "Enabled", accent: user.mfaEnabled !== false },
        { label: "SSO", value: user.ssoProvider || "OIDC" },
      ],
    },

    // ── Access tab ──
    permissions: [
      { label: "User Management", level: "Read" },
      { label: "Policy Management", level: "Read/Write" },
      { label: "Node Monitoring", level: "Read" },
      { label: "Analytics", level: "Full Access" },
      { label: "Settings", level: "None" },
    ],

    // ── Sessions tab ──
    sessions: [
      { region: "us-east-1", status: "Active", ip: "10.0.2.41", duration: "2h 14m" },
      { region: "eu-west-1", status: "Active", ip: "10.0.3.87", duration: "45m" },
    ],

    // ── Activity tab ──
    activity: [
      { title: "Connected to us-east-1", time: "2 min ago" },
      { title: "Changed exit node to eu-west-1", time: "1 hr ago" },
      { title: "Logged in via SSO", time: "2 hrs ago" },
      { title: "Disconnected from ap-south-1", time: "Yesterday" },
    ],

    // ── Policies tab ──
    policies: [
      { name: "Default Access", type: "Access", state: "Enforced" },
    ],
  };
}
